/**
 * Andamento prezzi — serie storica ricalcoli (DEC-046).
 *
 * Tabella con evoluzione mensile: costi fissi, prezzo medio, costo medio,
 * margine medio. Collocata sotto il gruppo Amministrazione nella sidebar.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormatters } from '@lib/formatters';

/* ─────────────────────────── Utility ─────────────────────────── */

const authHeadersGet = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/* ─────────────────────────── Interfacce ─────────────────────────── */

interface AndamentoItem {
  anno: number;
  mese: number;
  totale_costi_fissi_mensili: string;
  num_articoli: number;
  delta_per_unita: string;
  margine_medio_proposto: string;
  variazione_media_pct: string;
  stato: string;
}

/* ─────────────────────────── Componente ─────────────────────────── */

const MESI = [
  '', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

const STATO_BADGE: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  proposto: 'secondary',
  approvato: 'default',
  rifiutato: 'destructive',
};

export const AndamentoPrezzi: React.FC = () => {
  const [dati, setDati] = useState<AndamentoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { currency } = useFormatters();

  const unitaId = localStorage.getItem('unita_organizzativa_id') ?? '';

  const fetchAndamento = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (unitaId) params.set('unita_organizzativa_id', unitaId);
      const res = await fetch(
        `/api/v1/prezzi/andamento?${params}`,
        { headers: authHeadersGet() },
      );
      if (res.ok) {
        setDati(await res.json());
      }
    } finally {
      setLoading(false);
    }
  }, [unitaId]);

  useEffect(() => {
    fetchAndamento();
  }, [fetchAndamento]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold font-heading">Andamento prezzi</h1>

      <Card>
        <CardHeader>
          <CardTitle>Evoluzione mensile costi e margini</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground">Caricamento...</p>
          ) : dati.length === 0 ? (
            <p className="text-muted-foreground">
              Nessun ricalcolo storico disponibile. Esegui il primo ricalcolo
              dalla sezione Ricalcolo margini.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Periodo</TableHead>
                  <TableHead className="text-right">Costi fissi</TableHead>
                  <TableHead className="text-right">Delta/unità</TableHead>
                  <TableHead className="text-right">Margine medio</TableHead>
                  <TableHead className="text-right">Variazione %</TableHead>
                  <TableHead className="text-right">Articoli</TableHead>
                  <TableHead>Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dati.map((item) => (
                  <TableRow key={`${item.anno}-${item.mese}`}>
                    <TableCell>
                      {MESI[item.mese]} {item.anno}
                    </TableCell>
                    <TableCell className="text-right">
                      {currency(Number(item.totale_costi_fissi_mensili))}
                    </TableCell>
                    <TableCell className="text-right">
                      {currency(Number(item.delta_per_unita))}
                    </TableCell>
                    <TableCell className="text-right">
                      {Number(item.margine_medio_proposto).toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right">
                      {Number(item.variazione_media_pct).toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right">
                      {item.num_articoli}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATO_BADGE[item.stato] ?? 'outline'}>
                        {item.stato}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
